import type { Country } from '@/constants/config';
import type { CampaignName } from '@/constants/SE';

export const endpoints = {
  credit: {
    actions: '/credit/actions',
    amount: '/credit/amount',
    featuresSteps: '/credit/features-steps',
    overdue: '/credit/overdue',
    tierMigration: {
      root: '/credit/tier-migration',
      tierMigrationId: (tierMigrationId = ':tierMigrationId') =>
        `/credit/tier-migration/${tierMigrationId}` as const,
    },
  },
  login: {
    root: '/login',
    sessionId: (sessionId = ':sessionId') =>
      ({
        root: `/login/${sessionId}`,
        sync: `/login/${sessionId}/sync`,
      }) as const,
    authenticate: '/login/authenticate',
  },
  paymentProvider: {
    account: '/payment-provider/account',
    directPayment: {
      paymentId: (paymentId = ':paymentId') =>
        ({
          root: `/payment-provider/direct-payment/${paymentId}`,
          paymentRequest: `/payment-provider/direct-payment/${paymentId}/payment-request`,
          generateQr: `/payment-provider/direct-payment/${paymentId}/generate-qr`,
        }) as const,
    },
    withdrawal: {
      fullAmount: '/payment-provider/withdrawal/full-amount',
    },
  },
  simulate: {
    user: {
      generateToken: '/simulate/user/generate-token',
    },
    createBusiness: (country: Country) => `/simulate/business/${country.toLowerCase()}` as const,
    productTiers: '/simulate/product-tiers',
    embeddedPartners: (country: Country) =>
      `/simulate/business/${country.toLowerCase()}/embedded-partners` as const,
  },
  user: {
    addonProducts: {
      idProtect: '/user/addon-products/id-protect',
      paymentProtectionInsurance: '/user/addon-products/payment-protection-insurance',
    },
    campaigns: {
      campaign: (name: CampaignName) => `/user/campaigns/${name}`,
      activate: (name: CampaignName) => `/user/campaigns/${name}/activate`,
      decline: (name: CampaignName) => `/user/campaigns/${name}/decline`,
      viewed: (name: CampaignName) => `/user/campaigns/${name}/viewed`,
    },
    email: '/user/email',
    emailVerification: {
      send: '/user/email-verification/send',
      verify: '/user/email-verification/verify',
    },
    mobile: '/user/mobile',
    mobileVerification: {
      send: '/user/mobile-verification/send',
    },
    terminateAccount: '/user/terminate-account',
    terminateAccountDetails: '/user/terminate-account/details',
    update: '/user/update',
  },
  users: {
    me: '/users/me',
  },
  UK: {
    credit: {
      amount: '/uk/credit/amount',
      applications: {
        applicationId: (applicationId = ':applicationId') =>
          ({
            finalize: `/uk/credit/applications/${applicationId}/finalize`,
          }) as const,
      },
      productTierProjection: (productTierId = ':productTierId') =>
        `/uk/credit/${productTierId}/projection` as const,
    },
  },
  SE: {
    addonProducts: {
      idProtect: '/se/addon-products/id-protect',
      paymentProtectionInsurance: '/se/addon-products/payment-protection-insurance',
    },
    credit: {
      applications: {
        applicationId: (applicationId = ':applicationId') =>
          ({
            acceptOffer: `/se/credit/applications/${applicationId}/accept-offer`,
            signOffer: `/se/credit/applications/${applicationId}/sign-offer`,
            declineOffer: `/se/credit/applications/${applicationId}/decline-offer`,
          }) as const,
      },
    },
    loan: {
      applications: {
        applicationId: (applicationId = ':applicationId') =>
          ({
            signOffer: `/se/loan/applications/${applicationId}/sign-offer`,
            acceptOffer: `/se/loan/applications/${applicationId}/accept-offer`,
            declineOffer: `/se/loan/applications/${applicationId}/decline-offer`,
          }) as const,
      },
      featuresSteps: '/se/loan/features-steps',
      withdrawal: {
        status: '/se/loan/withdrawal/status',
      },
    },
  },
} as const;
